import { useState, useEffect, useCallback, useRef } from 'react';
import { X, SlidersHorizontal, RotateCcw, Check, Bell, ChevronDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/Dialog';
import { useListingsStore } from '../../stores/listings';
import { useAuthStore } from '../../stores/auth';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { api } from '../../services/api';
import type { ListingsParams, LiveSortBy } from '../../services/api';

const CONTRACT_TYPES = [
  { value: 'SALE', label: 'Vendita' },
  { value: 'RENT', label: 'Affitto' },
];

const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Appartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'VILLA', label: 'Villa' },
  { value: 'STUDIO', label: 'Monolocale' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Recenti' },
  { value: 'priceAsc', label: 'Prezzo ↑' },
  { value: 'price', label: 'Prezzo ↓' },
];

const PRICE_PRESETS_RENT = [
  { label: 'Fino a 500', min: 0, max: 500 },
  { label: '500 - 800', min: 500, max: 800 },
  { label: '800 - 1.200', min: 800, max: 1200 },
  { label: 'Oltre 1.200', min: 1200, max: undefined },
];

const PRICE_PRESETS_SALE = [
  { label: 'Fino a 150k', min: 0, max: 150000 },
  { label: '150k - 250k', min: 150000, max: 250000 },
  { label: '250k - 400k', min: 250000, max: 400000 },
  { label: 'Oltre 400k', min: 400000, max: undefined },
];

const ROOM_PRESETS = [
  { label: 'Mono', value: 1 },
  { label: 'Bilo', value: 2 },
  { label: 'Trilo', value: 3 },
  { label: '4+', value: 4 },
];

const DEFAULT_CITY = 'Milano';

// Helper: get label for active price filter
function getPriceLabel(minPrice?: number, maxPrice?: number, isRent?: boolean): string | null {
  if (!minPrice && !maxPrice) return null;
  const presets = isRent ? PRICE_PRESETS_RENT : PRICE_PRESETS_SALE;
  const match = presets.find(p => p.min === minPrice && p.max === maxPrice);
  if (match) return match.label;
  if (minPrice && maxPrice) return `${formatCompact(minPrice)}-${formatCompact(maxPrice)}`;
  if (minPrice) return `da ${formatCompact(minPrice)}`;
  if (maxPrice) return `fino ${formatCompact(maxPrice)}`;
  return null;
}

function formatCompact(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return `${n}`;
}

function getRoomsLabel(minRooms?: number, maxRooms?: number): string | null {
  if (!minRooms && !maxRooms) return null;
  if (minRooms === 4 && !maxRooms) return '4+';
  if (minRooms === maxRooms) {
    const preset = ROOM_PRESETS.find(p => p.value === minRooms);
    return preset?.label || `${minRooms}`;
  }
  return `${minRooms || 1}-${maxRooms || ''}`;
}

interface FilterPanelProps {
  className?: string;
  onFiltersApplied?: () => void;
}

export function FilterPanel({ className, onFiltersApplied }: FilterPanelProps) {
  const { filters, setFilters, resetFilters, fetchListings } = useListingsStore();
  const { isAuthenticated } = useAuthStore();
  const { subscribe: subscribePush, isSubscribed: isPushSubscribed } = usePushNotifications();
  const [localFilters, setLocalFilters] = useState<ListingsParams & { liveSortBy?: LiveSortBy }>(filters);
  const [isOpen, setIsOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // Popover state for desktop
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Close popover on outside click
  useEffect(() => {
    if (!openPopover) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPopover]);

  const handleApplyFilters = useCallback(async () => {
    setIsApplying(true);
    try {
      setFilters({ ...localFilters, page: 1 });
      await fetchListings();
      setIsOpen(false);
      onFiltersApplied?.();
    } finally {
      setIsApplying(false);
    }
  }, [localFilters, setFilters, fetchListings, onFiltersApplied]);

  const handleResetFilters = useCallback(async () => {
    setIsApplying(true);
    try {
      resetFilters();
      setLocalFilters({ city: DEFAULT_CITY, liveSortBy: 'date' });
      await fetchListings();
      setIsOpen(false);
      onFiltersApplied?.();
    } finally {
      setIsApplying(false);
    }
  }, [resetFilters, fetchListings, onFiltersApplied]);

  // Desktop: apply a filter change immediately
  const applyImmediate = useCallback(async (newFilters: Partial<ListingsParams & { liveSortBy?: LiveSortBy }>) => {
    const updated = { ...filters, ...newFilters, page: 1 };
    setLocalFilters(updated);
    setFilters(updated);
    await fetchListings();
  }, [filters, setFilters, fetchListings]);

  const handleCreateAlert = useCallback(async () => {
    if (!isAuthenticated) {
      setAlertMessage('Accedi per creare un avviso');
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    setIsCreatingAlert(true);
    setAlertMessage(null);

    try {
      if (!isPushSubscribed) {
        const subscribed = await subscribePush();
        if (!subscribed) {
          setAlertMessage('Abilita le notifiche per ricevere avvisi');
          return;
        }
      }

      const city = localFilters.city || 'Milano';
      const name = [
        localFilters.contractType === 'RENT' ? 'Affitto' : localFilters.contractType === 'SALE' ? 'Vendita' : 'Tutti',
        city,
        localFilters.minPrice || localFilters.maxPrice
          ? `${localFilters.minPrice || 0}-${localFilters.maxPrice || '...'} EUR`
          : null,
      ].filter(Boolean).join(' - ');

      await api.createSavedSearch({
        name,
        filters: { ...localFilters },
        notifyEnabled: true,
      });

      setAlertMessage('Avviso creato!');
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (err) {
      console.error('Failed to create alert:', err);
      setAlertMessage('Errore nella creazione');
      setTimeout(() => setAlertMessage(null), 3000);
    } finally {
      setIsCreatingAlert(false);
    }
  }, [isAuthenticated, isPushSubscribed, subscribePush, localFilters]);

  // Count active filters
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (['page', 'limit', 'sortBy', 'sortOrder', 'liveSortBy'].includes(key)) return false;
    if (value === undefined || value === null || value === '') return false;
    if (key === 'city' && value === DEFAULT_CITY) return false;
    return true;
  }).length;

  const isRent = localFilters.contractType === 'RENT' || !localFilters.contractType;
  const pricePresets = isRent ? PRICE_PRESETS_RENT : PRICE_PRESETS_SALE;

  const isPricePresetActive = (preset: { min: number; max: number | undefined }) => {
    return localFilters.minPrice === preset.min && localFilters.maxPrice === preset.max;
  };

  const isRoomPresetActive = (value: number) => {
    if (value === 4) return localFilters.minRooms === 4 && !localFilters.maxRooms;
    return localFilters.minRooms === value && localFilters.maxRooms === value;
  };

  // Chip component for modal
  const Chip = ({ label, isActive, onClick, disabled }: {
    label: string; isActive: boolean; onClick: () => void; disabled?: boolean;
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all min-h-[44px] ${
        isActive
          ? 'bg-blue-500 text-white shadow-sm'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {label}
    </button>
  );

  // Desktop filter chip button
  const FilterChip = ({ label, value, isActive, onClick }: {
    label: string; value?: string | null; isActive: boolean; onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all border whitespace-nowrap ${
        isActive
          ? 'bg-blue-50 border-blue-300 text-blue-700'
          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
      }`}
    >
      <span>{value || label}</span>
      <ChevronDown className="w-3.5 h-3.5 opacity-50" />
    </button>
  );

  // Full filter content (shared between mobile dialog and desktop "More" dialog)
  const FilterContent = () => (
    <div className="space-y-6">
      {/* Contract Type */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Tipo annuncio</label>
        <div className="flex gap-2">
          <Chip label="Tutti" isActive={!localFilters.contractType}
            onClick={() => setLocalFilters({ ...localFilters, contractType: undefined, minPrice: undefined, maxPrice: undefined })} disabled={isApplying} />
          {CONTRACT_TYPES.map((type) => (
            <Chip key={type.value} label={type.label} isActive={localFilters.contractType === type.value}
              onClick={() => setLocalFilters({ ...localFilters, contractType: type.value, minPrice: undefined, maxPrice: undefined })} disabled={isApplying} />
          ))}
        </div>
      </div>

      {/* Budget */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Budget</label>
        <div className="flex flex-wrap gap-2">
          <Chip label="Qualsiasi" isActive={!localFilters.minPrice && !localFilters.maxPrice}
            onClick={() => setLocalFilters({ ...localFilters, minPrice: undefined, maxPrice: undefined })} disabled={isApplying} />
          {pricePresets.map((preset) => (
            <Chip key={preset.label} label={preset.label} isActive={isPricePresetActive(preset)}
              onClick={() => setLocalFilters({ ...localFilters, minPrice: preset.min, maxPrice: preset.max })} disabled={isApplying} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Input type="number" placeholder="Min" min={0} value={localFilters.minPrice || ''} className="h-12" disabled={isApplying}
            onChange={(e) => setLocalFilters({ ...localFilters, minPrice: e.target.value ? parseInt(e.target.value) : undefined })} />
          <Input type="number" placeholder="Max" min={0} value={localFilters.maxPrice || ''} className="h-12" disabled={isApplying}
            onChange={(e) => setLocalFilters({ ...localFilters, maxPrice: e.target.value ? parseInt(e.target.value) : undefined })} />
        </div>
      </div>

      {/* Rooms */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Locali</label>
        <div className="flex flex-wrap gap-2">
          <Chip label="Tutti" isActive={!localFilters.minRooms && !localFilters.maxRooms}
            onClick={() => setLocalFilters({ ...localFilters, minRooms: undefined, maxRooms: undefined })} disabled={isApplying} />
          {ROOM_PRESETS.map((preset) => (
            <Chip key={preset.label} label={preset.label} isActive={isRoomPresetActive(preset.value)}
              onClick={() => {
                if (preset.value === 4) setLocalFilters({ ...localFilters, minRooms: 4, maxRooms: undefined });
                else setLocalFilters({ ...localFilters, minRooms: preset.value, maxRooms: preset.value });
              }} disabled={isApplying} />
          ))}
        </div>
      </div>

      {/* Property Type */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Tipo immobile</label>
        <div className="flex flex-wrap gap-2">
          <Chip label="Tutti" isActive={!localFilters.propertyType}
            onClick={() => setLocalFilters({ ...localFilters, propertyType: undefined })} disabled={isApplying} />
          {PROPERTY_TYPES.map((type) => (
            <Chip key={type.value} label={type.label} isActive={localFilters.propertyType === type.value}
              onClick={() => setLocalFilters({ ...localFilters, propertyType: type.value })} disabled={isApplying} />
          ))}
        </div>
      </div>

      {/* City */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Citta</label>
        <Input type="text" placeholder="Es. Milano, Roma..." value={localFilters.city || ''} className="h-12" disabled={isApplying}
          onChange={(e) => setLocalFilters({ ...localFilters, city: e.target.value || undefined })} />
      </div>

      {/* Advanced features */}
      <details className="group">
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between py-2">
          Altre caratteristiche
          <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
        </summary>
        <div className="pt-3 space-y-3">
          <div>
            <label className="text-sm text-gray-600 mb-2 block">Superficie (m2)</label>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Min" min={0} value={localFilters.minSurface || ''} className="h-12" disabled={isApplying}
                onChange={(e) => setLocalFilters({ ...localFilters, minSurface: e.target.value ? parseInt(e.target.value) : undefined })} />
              <Input type="number" placeholder="Max" min={0} value={localFilters.maxSurface || ''} className="h-12" disabled={isApplying}
                onChange={(e) => setLocalFilters({ ...localFilters, maxSurface: e.target.value ? parseInt(e.target.value) : undefined })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'hasElevator', label: 'Ascensore' },
              { key: 'hasTerrace', label: 'Terrazzo' },
              { key: 'isFurnished', label: 'Arredato' },
              { key: 'hasGarden', label: 'Giardino' },
            ].map((feature) => (
              <label key={feature.key}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all min-h-[52px] ${
                  (localFilters as any)[feature.key]
                    ? 'bg-blue-50 border-2 border-blue-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                } ${isApplying ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <input type="checkbox" checked={(localFilters as any)[feature.key] || false} className="sr-only" disabled={isApplying}
                  onChange={(e) => setLocalFilters({ ...localFilters, [feature.key]: e.target.checked || undefined } as any)} />
                <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                  (localFilters as any)[feature.key] ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300'
                }`}>
                  {(localFilters as any)[feature.key] && <Check className="w-3 h-3" />}
                </span>
                <span className="text-sm font-medium text-gray-700">{feature.label}</span>
              </label>
            ))}
          </div>
        </div>
      </details>

      {/* Sort */}
      <div>
        <label className="text-sm font-semibold text-gray-900 mb-3 block">Ordina per</label>
        <div className="flex flex-wrap gap-2">
          {SORT_OPTIONS.map((option) => (
            <Chip key={option.value} label={option.label}
              isActive={(localFilters as any).liveSortBy === option.value || (!(localFilters as any).liveSortBy && option.value === 'date')}
              onClick={() => setLocalFilters({ ...localFilters, liveSortBy: option.value } as any)} disabled={isApplying} />
          ))}
        </div>
      </div>
    </div>
  );

  // Labels for desktop chips
  const priceLabel = getPriceLabel(filters.minPrice, filters.maxPrice, isRent);
  const roomsLabel = getRoomsLabel(filters.minRooms, filters.maxRooms);
  const sortLabel = SORT_OPTIONS.find(o => o.value === ((filters as any).liveSortBy || 'date'))?.label || 'Recenti';

  // Count "extra" filters (beyond contract/price/rooms/sort)
  const extraCount = Object.entries(filters).filter(([key, value]) => {
    if (['page', 'limit', 'sortBy', 'sortOrder', 'liveSortBy', 'contractType', 'minPrice', 'maxPrice', 'minRooms', 'maxRooms'].includes(key)) return false;
    if (value === undefined || value === null || value === '') return false;
    if (key === 'city' && value === DEFAULT_CITY) return false;
    return true;
  }).length;

  return (
    <>
      {/* Mobile: compact filter button */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-full flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span>Filtri</span>
          {activeFiltersCount > 0 && (
            <span className="rounded-full bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Filter Dialog - fullscreen on mobile, centered on desktop */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="fixed inset-0 translate-x-0 translate-y-0 max-w-none gap-0 rounded-none shadow-none md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-none md:shadow-lg h-full md:h-auto md:max-h-[90vh] md:w-[480px] md:rounded-2xl p-0 border-0 flex flex-col bg-white [&>button:last-child]:hidden">
          <DialogTitle className="sr-only">Filtri di ricerca</DialogTitle>

          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10 safe-top">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Filtri</h2>
              {activeFiltersCount > 0 && (
                <span className="text-xs text-gray-500">{activeFiltersCount} attivi</span>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}
              className="h-10 w-10 rounded-full" aria-label="Chiudi filtri">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 pb-32">
            <FilterContent />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-4 py-3 safe-bottom">
            {alertMessage && (
              <div className="mb-2 text-center text-sm text-blue-600 font-medium">{alertMessage}</div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-none h-11 px-3" onClick={handleResetFilters} disabled={isApplying}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              <Button variant="outline" className="flex-none h-11 px-3" onClick={handleCreateAlert} disabled={isCreatingAlert || isApplying}>
                <Bell className="w-4 h-4 mr-1" />
                {isCreatingAlert ? '...' : 'Avvisami'}
              </Button>
              <Button className="flex-1 h-11 text-base font-semibold" onClick={handleApplyFilters} disabled={isApplying}>
                {isApplying ? 'Caricamento...' : 'Mostra risultati'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Desktop: Minimal filter bar */}
      <div className={`hidden md:flex items-center gap-2 ${className || ''}`} ref={popoverRef}>
        {/* Contract type - inline chips */}
        <div className="flex items-center rounded-full border border-gray-200 bg-white overflow-hidden">
          {[
            { value: undefined, label: 'Tutti' },
            ...CONTRACT_TYPES,
          ].map((type) => {
            const isActive = type.value === undefined
              ? !filters.contractType
              : filters.contractType === type.value;
            return (
              <button key={type.label} type="button"
                onClick={() => applyImmediate({
                  contractType: type.value,
                  minPrice: undefined,
                  maxPrice: undefined,
                })}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {type.label}
              </button>
            );
          })}
        </div>

        {/* City */}
        <div className="relative">
          <FilterChip
            label="Citta"
            value={filters.city && filters.city !== DEFAULT_CITY ? filters.city : null}
            isActive={!!filters.city && filters.city !== DEFAULT_CITY}
            onClick={() => setOpenPopover(openPopover === 'city' ? null : 'city')}
          />
          {openPopover === 'city' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-48">
              <Input
                type="text"
                placeholder="Es. Milano, Roma..."
                value={localFilters.city || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, city: e.target.value || undefined })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyImmediate({ city: localFilters.city || undefined });
                    setOpenPopover(null);
                  }
                }}
                className="h-9"
                autoFocus
              />
              <Button size="sm" className="w-full mt-2 h-8" onClick={() => {
                applyImmediate({ city: localFilters.city || undefined });
                setOpenPopover(null);
              }}>
                Applica
              </Button>
            </div>
          )}
        </div>

        {/* Budget */}
        <div className="relative">
          <FilterChip
            label="Budget"
            value={priceLabel}
            isActive={!!priceLabel}
            onClick={() => setOpenPopover(openPopover === 'price' ? null : 'price')}
          />
          {openPopover === 'price' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-56">
              <div className="flex flex-wrap gap-1.5 mb-2">
                <button type="button" onClick={() => { applyImmediate({ minPrice: undefined, maxPrice: undefined }); setOpenPopover(null); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${!filters.minPrice && !filters.maxPrice ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  Tutti
                </button>
                {pricePresets.map(p => (
                  <button key={p.label} type="button" onClick={() => { applyImmediate({ minPrice: p.min, maxPrice: p.max }); setOpenPopover(null); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${isPricePresetActive(p) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input type="number" placeholder="Min" min={0} value={localFilters.minPrice || ''} className="h-8 text-xs"
                  onChange={(e) => setLocalFilters({ ...localFilters, minPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { applyImmediate({ minPrice: localFilters.minPrice, maxPrice: localFilters.maxPrice }); setOpenPopover(null); } }} />
                <Input type="number" placeholder="Max" min={0} value={localFilters.maxPrice || ''} className="h-8 text-xs"
                  onChange={(e) => setLocalFilters({ ...localFilters, maxPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { applyImmediate({ minPrice: localFilters.minPrice, maxPrice: localFilters.maxPrice }); setOpenPopover(null); } }} />
              </div>
            </div>
          )}
        </div>

        {/* Rooms */}
        <div className="relative">
          <FilterChip
            label="Locali"
            value={roomsLabel}
            isActive={!!roomsLabel}
            onClick={() => setOpenPopover(openPopover === 'rooms' ? null : 'rooms')}
          />
          {openPopover === 'rooms' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
              <div className="flex gap-1.5">
                <button type="button" onClick={() => { applyImmediate({ minRooms: undefined, maxRooms: undefined }); setOpenPopover(null); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${!filters.minRooms && !filters.maxRooms ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  Tutti
                </button>
                {ROOM_PRESETS.map(p => (
                  <button key={p.label} type="button"
                    onClick={() => {
                      const f = p.value === 4
                        ? { minRooms: 4, maxRooms: undefined }
                        : { minRooms: p.value, maxRooms: p.value };
                      applyImmediate(f);
                      setOpenPopover(null);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${isRoomPresetActive(p.value) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sort */}
        <div className="relative">
          <FilterChip label="Ordina" value={sortLabel} isActive={false}
            onClick={() => setOpenPopover(openPopover === 'sort' ? null : 'sort')} />
          {openPopover === 'sort' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
              {SORT_OPTIONS.map(o => (
                <button key={o.value} type="button"
                  onClick={() => { applyImmediate({ liveSortBy: o.value } as any); setOpenPopover(null); }}
                  className={`block w-full text-left px-3 py-1.5 rounded text-sm ${
                    ((filters as any).liveSortBy || 'date') === o.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* More filters */}
        <button type="button" onClick={() => setIsOpen(true)}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
            extraCount > 0
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
          }`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span>Filtri</span>
          {extraCount > 0 && (
            <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">{extraCount}</span>
          )}
        </button>

        {/* Alert */}
        <button type="button" onClick={handleCreateAlert} disabled={isCreatingAlert}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all whitespace-nowrap disabled:opacity-50">
          <Bell className="w-3.5 h-3.5" />
          <span>{isCreatingAlert ? '...' : alertMessage || 'Avvisami'}</span>
        </button>

        {/* Reset */}
        {activeFiltersCount > 0 && (
          <button type="button" onClick={handleResetFilters}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <X className="w-3.5 h-3.5" />
            Reset
          </button>
        )}
      </div>
    </>
  );
}
